/**
 * Role permission management types
 */

import type { UserRole, RoleCategory } from './user'

/**
 * Permission categories for organizing permissions in the UI
 */
export type PermissionCategory = 
  | 'Patients'
  | 'Appointments'
  | 'Templates'
  | 'Lab Results'
  | 'Billing'
  | 'Users'
  | 'Settings'
  | 'Role Permissions'
  | 'System'

/**
 * Permission structure: resource:action
 * Examples: patients:read, patients:write, appointments:read, etc.
 */
export type Permission = string

/**
 * Role permission definition
 */
export interface RolePermission {
  role: UserRole
  roleCategory: RoleCategory
  permissions: Permission[]
  description?: string
  createdAt?: string
  updatedAt?: string
}

/**
 * Permission template/preset for quick role setup
 */
export interface PermissionTemplate {
  id: string
  name: string
  description: string
  permissions: Permission[]
  category?: PermissionCategory[]
}

/**
 * Permission with metadata for display
 */
export interface PermissionWithMetadata {
  permission: Permission
  resource: string
  action: string
  category: PermissionCategory
  description?: string
}

/**
 * Helper function to parse permission string into resource and action
 */
export function parsePermission(permission: Permission): { resource: string; action: string } {
  if (permission === '*') {
    return { resource: '*', action: '*' }
  }
  const parts = permission.split(':')
  return {
    resource: parts[0] || '',
    action: parts[1] || '',
  }
}

/**
 * Helper function to get permission category based on resource
 */
export function getPermissionCategory(permission: Permission): PermissionCategory {
  if (permission === '*') {
    return 'System'
  }
  
  const { resource } = parsePermission(permission)
  
  const categoryMap: Record<string, PermissionCategory> = {
    patients: 'Patients',
    appointments: 'Appointments',
    templates: 'Templates',
    'lab-results': 'Lab Results',
    'billing-accounts': 'Billing',
    'billing-items': 'Billing',
    'billing-payments': 'Billing',
    'billing-statements': 'Billing',
    users: 'Users',
    settings: 'Settings',
    'role-permissions': 'Role Permissions',
  }
  
  return categoryMap[resource] || 'System'
}

/**
 * Helper function to get all permissions grouped by category
 */
export function groupPermissionsByCategory(permissions: Permission[]): Record<PermissionCategory, Permission[]> {
  const grouped: Record<PermissionCategory, Permission[]> = {
    'Patients': [],
    'Appointments': [],
    'Templates': [],
    'Lab Results': [],
    'Billing': [],
    'Users': [],
    'Settings': [],
    'Role Permissions': [],
    'System': [],
  }
  
  permissions.forEach((permission) => {
    const category = getPermissionCategory(permission)
    grouped[category].push(permission)
  })
  
  return grouped
}
